import { formatInteger } from '../utils/formatters';

// German UI strings — the source of truth. en.ts must provide the same shape
// (typed as Messages), so a key missing in English is a compile error.
// Group by component; functions take already-formatted values unless their
// parameter is typed `number` (then they format it themselves).
export const de = {
  common: {
    close: 'Schließen',
    cancel: 'Abbrechen',
    removeFilter: 'Filter entfernen',
  },

  // The two halves of the dataset: the tier toggle, the table titles and the
  // table toggle all use these.
  tiers: {
    physical: 'Investitionsprojekte',
    financial: 'Finanztransaktionen',
  },

  // Display names for the stored type_of_investment values (labels.ts
  // typeLabel). The stored values stay the keys; IPO, Funding and M&A show as
  // stored.
  investmentTypes: {
    greenfield: 'Greenfield-Investition',
    brownfield: 'Brownfield-Investition',
  },
  // Stored investment_status values (announced, in progress, ...) for display.
  investmentStatuses: {
    announced: 'Angekündigt',
    inProgress: 'In Umsetzung',
    finished: 'Fertiggestellt',
    cancelled: 'Abgebrochen',
  },

  app: {
    firstPublished: 'Erstveröffentlichung: 12. Juni 2026',
    lastUpdate: (date: string) => ` | Letztes Update: ${date}`,
    grouping: 'Gruppierung:',
    groupingNone: 'Keine',
    groupingCluster: 'Cluster',
    groupingSites: 'Standorte',
    visualisation: 'Visualisierung:',
    industries: 'Industrien',
    countries: 'Herkunftsländer',
    timeline: 'Zeitverlauf',
    drillHint: 'Klicke einen Balken, um in eine Untergruppe zu wechseln.',
    allIndustries: 'Alle Industrien',
    // Kinds of the chart-click selection chip.
    selectionWahlkreis: 'Wahlkreis',
    selectionCountry: 'Herkunftsland',
    selectionIndustry: 'Industrie',
    disclaimer:
      'Die Übersicht und der Datensatz zu den im Investitionstracker erfassten Investitionsvorhaben basiert auf Daten von S&P Global sowie auf einer automatisierten Abfrage der Google News API. Die Daten stützen sich auf Unternehmensankündigungen und Meldungen über Investitionen. Die so erfassten Meldungen und Datenpunkte werden mithilfe eines KI-gestützten Verfahrens gescannt und einzelnen Projekten sowie Standorten zugeordnet. Updates erfolgen dabei in einem Zyklus von zwei Wochen. Investitionen, die weder öffentlich gemacht wurden noch Teil der Berichterstattung sind oder waren, werden im Tracker nicht erfasst. Bei Daten, die vor dem Launch des Trackers liegen, wurde die Richtigkeit der Zuordnung stichprobenhaft geprüft; alle seit dem Launch erfassten Einträge werden zusätzlich einmalig manuell gesichtet und verifiziert. Trotz dieser Qualitätssicherung können wir weder die Vollständigkeit noch die Richtigkeit der dargestellten Daten garantieren. Insbesondere kann es bei der automatisierten Erfassung und KI-gestützten Zuordnung zu Fehlklassifikationen, Dopplungen oder Auslassungen einzelner Projekte kommen, etwa wenn Medienberichte unvollständig, widersprüchlich oder nachträglich korrigiert wurden. Angekündigte Investitionen spiegeln zudem nicht zwangsläufig tatsächlich realisierte Investitionsvolumina wider; Projekte können verschoben, angepasst oder eingestellt werden, ohne dass dies unmittelbar im Datensatz abgebildet ist. Die ausgewiesenen Beträge beruhen auf den jeweils verfügbaren öffentlichen Angaben und können von den tatsächlichen Investitionssummen abweichen. Obwohl Projekte über einen längeren Zeitraum dargestellt werden, kann es im Datensatz eine Übergewichtung von kürzlichen Investitionsprojekten geben. Dies liegt an der leichteren Verfügbarkeit aktueller Nachrichtenmeldungen und wird sich im Laufe der Zeit reduzieren. Der Datensatz dient ausschließlich Informationszwecken und stellt keine Anlageberatung, Investitionsempfehlung oder sonstige Empfehlung zum Kauf oder Verkauf von Finanzinstrumenten dar. Wir arbeiten kontinuierlich an der Verbesserung der Datenqualität und freuen uns über Hinweise auf fehlerhafte oder fehlende Einträge. Unter Angabe der korrekten Quellenangabe können Nutzer diese Daten frei nutzen, transformieren und verbreiten; etwaige Nutzungsbedingungen der zugrundeliegenden Datenquellen bleiben hiervon unberührt. Mithilfe der Daten vertretene Positionen geben die Position der Nutzer wieder, nicht aber die des Herausgebers.',
    missingProject: 'Fehlendes Projekt?',
    suggestProject: 'Investitionsprojekt vorschlagen',
    contact: 'Ansprechpartner:',
  },

  chips: {
    resetAllAria: 'Alle Filter zurücksetzen',
    resetAll: '↺ Alle zurücksetzen',
    caption: 'Aktive Filter:',
    clearSelection: 'Auswahl aufheben',
    period: 'Zeitraum',
    resetPeriod: 'Zeitraum zurücksetzen',
    search: 'Suche',
    removeSearch: 'Suche entfernen',
    quoted: (term: string) => `„${term}“`,
    keys: {
      industries: 'Industrie',
      federalStates: 'Bundesland',
      investmentStatuses: 'Status',
      companies: 'Unternehmen',
    },
  },

  filterPanel: {
    filter: 'Filter',
    closeFilter: 'Filter schließen',
    industry: 'Industrie',
    allIndustries: 'Alle Industrien',
    federalState: 'Bundesland',
    allFederalStates: 'Alle Bundesländer',
    status: 'Status',
    allStatuses: 'Alle Status',
    company: 'Unternehmen',
    allCompanies: 'Alle Unternehmen',
    reset: 'Zurücksetzen',
    done: 'Fertig',
  },

  // SearchableSelect + CascadingSelect.
  select: {
    selected: (n: number) => `${n} ausgewählt`,
    search: 'Suchen...',
    resultsOf: (shown: number, total: number) => `${shown} von ${total} Ergebnissen`,
    more: (n: number) => `${n} weitere — Suche eingrenzen`,
    noResults: 'Keine Ergebnisse',
    noResultsFor: (term: string) => `Keine Ergebnisse für "${term}"`,
  },

  timeRange: {
    label: 'Zeitraum',
    from: 'Zeitraum von',
    to: 'Zeitraum bis',
    months12: '12 Monate',
    years3: '3 Jahre',
    all: 'Alle',
  },

  dateField: {
    months: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    chooseDate: 'Datum wählen',
    year: 'Jahr',
    openCalendar: (field: string) => `${field}: Kalender öffnen`,
  },

  search: {
    placeholder: 'Projekte durchsuchen…',
    aria: 'Projekte durchsuchen',
    clear: 'Suche löschen',
  },

  // RankedBarChart + TimeSeriesChart.
  charts: {
    count: 'Anzahl',
    volume: 'Volumen',
    exportImage: 'Diagramm als Bild exportieren',
    breadcrumbAria: 'Industrie-Drilldown',
    noData: 'Keine Daten',
    total: 'Gesamt',
    // Unit caption over the time series' volume axis.
    axisUnit: { bn: 'Mrd. €', m: 'Mio. €', k: 'Tsd. €', one: '€' },
  },

  table: {
    entries: (n: number) => `${n} Einträge`,
    exportCsv: 'Als CSV exportieren',
    mapSelection: 'Kartenauswahl aktiv —',
    investments: (n: number) => `${n} Investitionen`,
    clearFilter: 'Filter aufheben',
    company: 'Unternehmen',
    project: 'Projekt',
    industry: 'Industrie',
    type: 'Investitionsart',
    /** Card-layout label of the type cell (phones). */
    typeShort: 'Art',
    volume: 'Volumen',
    firstDate: 'Erste Meldung',
    lastUpdate: 'Letztes Update',
    projectStart: 'Projektstart',
    projectEnd: 'Projektende',
    location: 'Standort',
    status: 'Status',
    range: (from: number, to: number, total: number) => `${from}–${to} von ${total}`,
    previous: '‹ Zurück',
    next: 'Weiter ›',
  },

  modal: {
    loadingHistory: 'Lade Verlauf...',
    history: (n: number) => `Verlauf (${n} ${n === 1 ? 'Eintrag' : 'Einträge'})`,
    source: 'Quelle:',
    errorInEntry: 'Fehler in diesem Eintrag?',
    suggestCorrection: 'Korrektur vorschlagen',
  },

  news: {
    label: 'Aktuelle Meldungen',
    labelShort: 'Aktuell',
  },

  spotlights: {
    title: 'Spotlights',
  },

  map: {
    exitFullscreen: 'Vollbild schließen',
    enterFullscreen: 'Karte als Vollbild',
    exportImage: 'Karte als Bild exportieren',
    typeLegend: 'Investitionsart',
    volumeLegend: 'Investitionsvolumen',
    onlyThisType: 'Klick: nur diesen Typ anzeigen',
    investments: (n: number) => `${n} Investition${n === 1 ? '' : 'en'}`,
    // Choropleth legend bins; the number arrives preformatted.
    compactVolume: {
      bn: (n: string) => `${n} Mrd.`,
      m: (n: string) => `${n} Mio.`,
      k: (n: string) => `${n} Tsd.`,
    },
    exporting: 'Karte wird exportiert …',
    twoFingers: 'Zum Bewegen der Karte zwei Finger benutzen',
  },

  // PNG and CSV exports.
  exports: {
    source: 'Quelle: dezernatzukunft.org/daten/investitionstracker',
    chartFilePrefix: 'investitionen',
    mapFileName: 'investitionskarte',
    csvFileName: (date: string) => `investitionsprojekte_export_${date}.csv`,
    csvHeaders: {
      company: 'Unternehmen',
      project: 'Projekt',
      industry: 'Industrie',
      type: 'Investitionsart',
      volume: 'Volumen_EUR',
      jobs: 'Arbeitsplaetze',
      firstDate: 'Erste_Meldung',
      lastUpdate: 'Letztes_Update',
      projectStart: 'Projektstart',
      projectEnd: 'Projektende',
      location: 'Standort',
      status: 'Status',
    },
  },

  exportDialog: {
    title: 'Daten exportieren',
    loading: 'Daten werden geladen...',
    building: 'CSV wird erstellt...',
    failed: 'Export fehlgeschlagen',
    currentView: 'Aktuelle Ansicht',
    currentViewDescription: (n: number) => `${formatInteger(n)} Einträge mit aktuellen Filtern`,
    allData: 'Alle Daten',
    allDataDescription: 'Alle Investitionsprojekte ohne Filter exportieren',
    export: 'Exportieren',
  },

  feedback: {
    titleCorrection: 'Korrektur vorschlagen',
    titleNewProject: 'Investitionsprojekt vorschlagen',
    errors: {
      company: 'Bitte geben Sie das Unternehmen an.',
      city: 'Bitte geben Sie den Ort an.',
      cityPick: 'Bitte wählen Sie den Ort aus der Vorschlagsliste, damit wir das Projekt korrekt auf der Karte verorten können.',
      sourceCorrection: 'Bitte geben Sie einen Link zur Quelle an, die Ihre Korrektur belegt.',
      source: 'Bitte geben Sie einen Link zur Quelle an.',
      sourceUrl: 'Der Quellen-Link muss mit http:// oder https:// beginnen.',
      email: 'Bitte geben Sie eine gültige E-Mail-Adresse an (oder lassen Sie das Feld leer).',
      volume: 'Bitte geben Sie das Investitionsvolumen als Zahl in Mio. € an.',
      jobs: 'Bitte geben Sie die Arbeitsplätze als ganze Zahl an.',
      init: 'Das Formular konnte nicht initialisiert werden. Bitte schließen Sie den Dialog und versuchen Sie es erneut.',
    },
    sending: 'Ihr Hinweis wird gesendet…',
    thanks: 'Vielen Dank für Ihren Hinweis!',
    reviewNote: 'Wir prüfen jede Meldung manuell, in der Regel innerhalb weniger Tage.',
    /** Followed by the contact address as a mailto link, then a full stop. */
    sendFailed: 'Senden fehlgeschlagen. Bitte versuchen Sie es später erneut oder schreiben Sie uns an',
    backToForm: 'Zurück zum Formular',
    introCorrection: 'Sie haben einen Fehler in diesem Eintrag entdeckt? Korrigieren Sie einfach die betroffenen Felder — wir prüfen jede Meldung manuell, bevor Daten geändert werden.',
    introNewProject: 'Ihnen fehlt ein Investitionsprojekt in unserer Übersicht? Schlagen Sie es hier vor — wir prüfen jeden Vorschlag manuell, bevor er aufgenommen wird.',
    company: 'Unternehmen *',
    companyPlaceholder: 'z. B. Beispiel GmbH',
    project: 'Projektname (optional)',
    projectPlaceholder: 'z. B. Neues Batteriewerk',
    cityRequired: 'Ort *',
    city: 'Ort',
    cityPlaceholder: 'z. B. Leipzig — bitte aus der Liste wählen',
    cityRecognised: (place: string) => `✓ Ort erkannt: ${place} — wird auf der Karte verortet.`,
    cityLookupDown: 'Ortssuche derzeit nicht erreichbar — der Ort wird bei der Prüfung zugeordnet.',
    multipleLocations: 'Dieser Eintrag hat mehrere Standorte; hier wird der erste angezeigt.',
    federalState: 'Bundesland (optional)',
    pleaseChoose: 'Bitte wählen…',
    type: 'Art der Investition (optional)',
    status: 'Status (optional)',
    volume: 'Investitionsvolumen in Mio. € (optional)',
    volumePlaceholder: 'z. B. 250',
    jobs: 'Neue Arbeitsplätze (optional)',
    jobsPlaceholder: 'z. B. 500',
    date: 'Datum der Ankündigung (optional)',
    source: 'Link zur Quelle *',
    sourceHintCorrection: 'Erforderlich: ein öffentlicher Beleg für Ihre Korrektur, z. B. Pressemitteilung oder Zeitungsartikel.',
    sourceHint: 'Erforderlich: ein öffentlicher Beleg, z. B. Pressemitteilung oder Zeitungsartikel.',
    explanation: 'Erläuterung (optional)',
    description: 'Beschreibung (optional)',
    messagePlaceholderCorrection: 'Was haben Sie korrigiert, und woher wissen Sie es?',
    messagePlaceholder: 'Weitere Details zum Projekt…',
    email: 'E-Mail (optional)',
    emailPlaceholder: 'ihre@email.de',
    emailHint: 'Nur für Rückfragen. Sie erhalten keine automatischen E-Mails.',
    submit: 'Absenden',
    // Labels for the stored enum values (the values themselves stay English).
    types: {
      greenfield: 'Neubau (Greenfield)',
      brownfield: 'Erweiterung bestehender Anlagen (Brownfield)',
    },
    statuses: {
      announced: 'Angekündigt',
      inProgress: 'In Umsetzung',
      finished: 'Fertiggestellt',
      cancelled: 'Abgebrochen',
    },
  },

  city: {
    searching: 'Suche…',
    unavailable: 'Ortssuche nicht erreichbar — Sie können den Ort auch frei eintragen.',
    noHits: 'Keine Treffer.',
  },

  // schema.org Dataset markup (structuredData.ts).
  dataset: {
    name: 'Investitionstracker',
    description:
      'Angekündigte Investitionsprojekte privater Unternehmen in Deutschland — '
      + 'nach Branche, Bundesland, Investitionsart und Volumen. '
      + 'Frei durchsuchbar, laufend aktualisiert.',
    country: 'Deutschland',
    variables: [
      'Investitionsvolumen',
      'Geschaffene Arbeitsplätze',
      'Investitionsart',
      'Projektstatus',
      'Branche',
      'Standort',
    ],
  },
};

export type Messages = typeof de;
